import { Suspense, useEffect, useState } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { NgoCampaignCard } from './ngo-campaign-card';
import { NgoCampaignsGridSkeleton } from './ngo-campaign-card-skeleton';
import type { PartnerNgo } from './use-partner-ngos';
import { NewPathsIllustration } from '@worldcoo/ui/illustrations/static/new-paths';

/** The backend answers at most three (September 2026 review), but that limit is the backend's to change. */
const CAMPAIGNS_PER_PAGE = 4;

function BarButton(props: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={props.onClick}
			disabled={props.disabled}
			aria-label={props.label}
			className="text-ink grid size-8 shrink-0 cursor-pointer place-items-center rounded-full bg-white transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-40"
		>
			{props.children}
		</button>
	);
}

/** Its own component, so the Suspense boundary covers the grid alone. */
function CampaignsGrid(props: { ngo: PartnerNgo; page: number; onPageCount: (count: number) => void }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { baseUrl } = useRouteContext({ from: '__root__' });
	const { data } = useSuspenseQuery(
		trpc.portal.getPartnerNgoCampaigns.queryOptions({ ngoId: props.ngo.id, locale: language }),
	);

	const pageCount = Math.max(1, Math.ceil(data.campaigns.length / CAMPAIGNS_PER_PAGE));
	const onPageCount = props.onPageCount;
	useEffect(() => onPageCount(pageCount), [pageCount, onPageCount]);

	if (data.campaigns.length === 0) {
		// lingui disallows a property access inside t``.
		const name = props.ngo.name;
		const noCampaignsCopy = t`${name} has not had any campaign recently. Take a look at the other organisations in the directory.`;
		return (
			<div className="flex flex-col items-center gap-4 py-16 text-center">
				<NewPathsIllustration className="h-auto w-[200px]" />
				<div className="flex flex-col gap-1">
					<p className="text-xl leading-[1.3] font-medium">{t`No campaigns to show right now.`}</p>
					<p className="text-ink/60 text-sm leading-[1.5]">{noCampaignsCopy}</p>
				</div>
			</div>
		);
	}

	const start = props.page * CAMPAIGNS_PER_PAGE;
	return (
		<div className="grid gap-6 xl:grid-cols-2">
			{data.campaigns.slice(start, start + CAMPAIGNS_PER_PAGE).map((campaign) => (
				<NgoCampaignCard key={campaign.id} campaign={campaign} baseUrl={baseUrl} />
			))}
		</div>
	);
}

/** Radix primitives, as the shared `DialogContent` is a centred app dialog, not a full-bleed bottom sheet. */
export function NgoCampaignsModal(props: { ngo: PartnerNgo | undefined }) {
	const { t } = useLingui();
	const navigate = useNavigate();
	const [page, setPage] = useState(0);
	const [pageCount, setPageCount] = useState(1);
	const ngo = props.ngo;
	const ngoId = ngo?.id;

	// `replace`, so three partners opened in a row are not three back-steps to leave the page.
	const close = () => void navigate({ to: '/www/partner-ngos', replace: true, resetScroll: false });

	useEffect(() => {
		setPage(0);
		setPageCount(1);
	}, [ngoId]);

	return (
		<DialogPrimitive.Root open={!!ngo} onOpenChange={(open) => !open && close()}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-ink/25 fixed inset-0 z-50 backdrop-blur-[3px]" />
				{/* Or scrim clicks land on the full-viewport box; inline, as `DismissableLayer` writes it inline. */}
				<DialogPrimitive.Content
					className="data-open:animate-in data-closed:animate-out data-closed:slide-out-to-bottom data-open:slide-in-from-bottom fixed inset-x-0 top-8 bottom-0 z-50 flex flex-col gap-8 duration-200 outline-none"
					style={{ pointerEvents: 'none' }}
					aria-describedby={undefined}
				>
					{ngo && (
						/* Keyed, as a reused `<picture>` never re-picks from a changed `srcSet`. */
						<div key={ngo.id} className="contents">
							<div className="mx-auto flex w-full max-w-[1376px] shrink-0 items-center px-4">
								{/* Two permanently disabled arrows read as broken. */}
								{pageCount > 1 && (
									<div className="pointer-events-auto flex items-center gap-2">
										<BarButton
											label={t`Previous campaigns`}
											onClick={() => setPage((current) => current - 1)}
											disabled={page <= 0}
										>
											<ArrowLeft className="size-4" />
										</BarButton>
										<BarButton
											label={t`More campaigns`}
											onClick={() => setPage((current) => current + 1)}
											disabled={page >= pageCount - 1}
										>
											<ArrowRight className="size-4" />
										</BarButton>
									</div>
								)}
								{/* A wrapper, as `BarButton` does not spread the props `asChild` hands it. */}
								<div className="pointer-events-auto ml-auto">
									<DialogPrimitive.Close asChild>
										<BarButton label={t`Close`} onClick={close}>
											<X className="size-4" />
										</BarButton>
									</DialogPrimitive.Close>
								</div>
							</div>

							{/* Runs off the bottom of the viewport by design. */}
							<div className="pointer-events-auto flex-1 overflow-y-auto rounded-t-3xl bg-white">
								<div className="mx-auto max-w-[1440px] p-6 lg:p-8">
									<div className="flex items-center gap-5">
										{ngo.logo && (
											<div className="grid size-20 shrink-0 place-items-center rounded-lg bg-white p-3 shadow-[0_10.8px_28.8px_0_rgba(46,20,0,0.12)]">
												<Image
													src={ngo.logo.location}
													alt={ngo.name}
													height={56}
													aspectRatio={1.6}
													objectFit="contain"
													className="max-h-14 w-full object-contain!"
												/>
											</div>
										)}
										<DialogPrimitive.Title className="text-2xl leading-[1.3] font-medium md:text-[2rem]">
											{t`See the most recent campaigns`}
										</DialogPrimitive.Title>
									</div>

									<div className="mt-8">
										{/* A blank panel through a cold fetch reads as a partner with nothing to show. */}
										<Suspense fallback={<NgoCampaignsGridSkeleton />}>
											<CampaignsGrid ngo={ngo} page={page} onPageCount={setPageCount} />
										</Suspense>
									</div>
								</div>
							</div>
						</div>
					)}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
